import { FC } from 'react'
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import { MicNone as MicIcon } from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'

import { Layout } from '@/apps/latest/components/templates/Layout'
import {
  HomecarePatientPanel,
  sectionHeadingSx,
} from '@/apps/latest/features/home-visit/HomecarePatientPanel'
import { type CarePlanSummary } from '@/apps/latest/features/home-visit/carePlanApi'
import { monthDay } from '@/apps/latest/features/home-visit/carePlanDates'
import { type HomecareVisit } from '@/apps/latest/features/home-visit/homecareApi'
import { useReviewCarePlan } from '@/apps/latest/features/home-visit/useCarePlan'
import {
  useCreateHomecareVisit,
  useEndHomecarePatient,
  useHomecarePatient,
  useRegisterHomecarePatient,
} from '@/apps/latest/features/home-visit/useHomecare'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { useToast } from '@/shared/contexts/ToastContext'
import { RADIUS } from '@/shared/styles/radii'
import { scaledFontSize } from '@/shared/styles/typography'

const formatDate = (iso: string) => {
  const d = new Date(iso)
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`
}

// 訪問の行の状態。報告書が着地したかどうかが店にとっての区切り。
export const visitStateLabel = (visit: HomecareVisit): string => {
  if (visit.generated_at || visit.edited_at) return '報告書あり'
  if (visit.status === 'pending') return '録音前'
  if (visit.status === 'recording') return '録音中'
  if (visit.status === 'failed' || visit.status === 'failed_short') {
    return '録り直しが必要'
  }
  return '作成中'
}

// 計画書の行の字 (最新の版・作成日・予定日)。書いている途中はその旨だけ
export const planRowLabel = (plan: CarePlanSummary): string => {
  if (plan.is_generating) return `第${plan.version}版 作っています`
  const dates = plan.scheduled_dates.map(monthDay).join(' ')
  return [
    `第${plan.version}版`,
    `${monthDay(plan.created_at.slice(0, 10))} 作成`,
    dates ? `予定 ${dates}` : '',
  ]
    .filter(Boolean)
    .join(' ・ ')
}

const rowSx = {
  justifyContent: 'flex-start',
  gap: 2,
  minHeight: 32,
  px: 1,
  borderRadius: `${RADIUS.md}px`,
  '&:hover, &.Mui-focusVisible': {
    bgcolor: colorTokens.bg.muted,
  },
} as const

const mutedLabelSx = {
  fontSize: scaledFontSize('label'),
  color: colorTokens.fg.muted,
} as const

// 患者のページ (/homecare/patients/:patientId): 患者の欄・報告書の宛先・計画書・訪問。
// 在宅の録音の入口はここの［訪問を記録］だけ (処方の無い訪問だけの日も同じ)。
// 押すと録音待ちの器ができ、訪問のページ (/homecare/visits/:id) で録る。
// 最後の「在宅の一覧から外す」は左の一覧から消すだけで、訪問の記録とこのページは残る (［一覧に戻す］で戻る)。
const HomecarePatientPage: FC = () => {
  const { patientId } = useParams<{ patientId: string }>()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { data: patient, isLoading } = useHomecarePatient(patientId ?? null)
  const createVisit = useCreateHomecareVisit()
  const end = useEndHomecarePatient()
  const register = useRegisterHomecarePatient()
  const review = useReviewCarePlan(patientId ?? '')
  const planPath = `/homecare/patients/${patientId}/plan`

  const recordVisit = async () => {
    if (!patientId) return
    try {
      const eventId = await createVisit.mutateAsync(patientId)
      navigate(`/homecare/visits/${eventId}`)
    } catch {
      showToast('訪問の記録を始められませんでした', 'error')
    }
  }

  return (
    <Layout breadcrumb={['在宅', patient?.name || '']}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : patient ? (
        <Stack spacing={1}>
          {patient.ended && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ maxWidth: 640, pb: 1 }}
            >
              <Typography
                component="p"
                sx={{
                  fontSize: scaledFontSize('bodySm'),
                  color: colorTokens.fg.muted,
                }}
              >
                在宅の一覧から外しています
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={register.isPending}
                onClick={() =>
                  register.mutate(patient.patient_id, {
                    onError: () =>
                      showToast('在宅の一覧に戻せませんでした', 'error'),
                  })
                }
              >
                一覧に戻す
              </Button>
            </Stack>
          )}
          <HomecarePatientPanel patient={patient} />
          {/* 計画書 (design-book ADR-0058)。患者のページは最新の版の 1 行だけ、中身は計画書のページ。
              外した人の版は増やさない (訪問と同じ。作るなら頭の［一覧に戻す］から) */}
          <Stack direction="row" alignItems="center" sx={{ maxWidth: 640 }}>
            <Typography component="h2" sx={sectionHeadingSx}>
              計画書
            </Typography>
            <Box sx={{ ml: 'auto', pt: 2 }}>
              {patient.ended ? null : patient.plan ? (
                <Button
                  variant="text"
                  disabled={review.isPending || patient.plan.is_generating}
                  onClick={() =>
                    review.mutate(undefined, {
                      onSuccess: () => navigate(planPath),
                    })
                  }
                >
                  見直す
                </Button>
              ) : (
                <Button variant="outlined" onClick={() => navigate(planPath)}>
                  計画書を作る
                </Button>
              )}
            </Box>
          </Stack>
          {patient.plan ? (
            <Stack spacing={0.25} sx={{ maxWidth: 640 }}>
              <ButtonBase onClick={() => navigate(planPath)} sx={rowSx}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: scaledFontSize('bodySm'),
                    color: colorTokens.fg.default,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {planRowLabel(patient.plan)}
                </Typography>
              </ButtonBase>
              {patient.plan.review_due && !patient.plan.is_generating && (
                <Typography component="p" sx={{ ...mutedLabelSx, px: 1 }}>
                  見直しの時期です
                </Typography>
              )}
            </Stack>
          ) : (
            // 外した人は［計画書を作る］を出さないので、空の節に (なし) を出す (訪問と同じ)
            patient.ended && (
              <Typography
                component="p"
                sx={{
                  px: 1,
                  fontSize: scaledFontSize('label'),
                  color: colorTokens.fg.muted,
                }}
              >
                (なし)
              </Typography>
            )
          )}
          <Stack direction="row" alignItems="center" sx={{ maxWidth: 640 }}>
            <Typography component="h2" sx={sectionHeadingSx}>
              訪問
            </Typography>
            {/* 外した人の訪問は増やさない (一覧に無い人の訪問は、訪問のページで左の行を選べない)。
                記録するなら頭の［一覧に戻す］から。design-book ADR-0056 */}
            {!patient.ended && (
              <Box sx={{ ml: 'auto', pt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<MicIcon />}
                  onClick={() => void recordVisit()}
                  disabled={createVisit.isPending}
                >
                  訪問を記録
                </Button>
              </Box>
            )}
          </Stack>
          {patient.visits.length > 0 ? (
            <Stack spacing={0.25} sx={{ maxWidth: 640 }}>
              {patient.visits.map((visit) => (
                <ButtonBase
                  key={visit.event_id}
                  onClick={() => navigate(`/homecare/visits/${visit.event_id}`)}
                  sx={rowSx}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: scaledFontSize('bodySm'),
                      color: colorTokens.fg.default,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatDate(visit.visited_at)}
                  </Typography>
                  <Typography component="span" sx={mutedLabelSx}>
                    {visitStateLabel(visit)}
                  </Typography>
                  {/* 計画に無い日の訪問だけ。判定は back (その時点で効いていた版の予定日) */}
                  {visit.off_plan && (
                    <Typography component="span" sx={mutedLabelSx}>
                      計画外
                    </Typography>
                  )}
                </ButtonBase>
              ))}
            </Stack>
          ) : (
            <Typography
              component="p"
              sx={{
                px: 1,
                fontSize: scaledFontSize('label'),
                color: colorTokens.fg.muted,
              }}
            >
              (なし)
            </Typography>
          )}
          {!patient.ended && (
            <Box sx={{ maxWidth: 640, pt: 3 }}>
              <Button
                variant="text"
                size="small"
                disabled={end.isPending}
                onClick={() =>
                  end.mutate(patient.id, {
                    onError: () =>
                      showToast('在宅の一覧から外せませんでした', 'error'),
                  })
                }
                sx={{ color: colorTokens.fg.muted }}
              >
                在宅の一覧から外す
              </Button>
            </Box>
          )}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          在宅の患者が見つかりません。
        </Typography>
      )}
    </Layout>
  )
}

export default HomecarePatientPage
